import { useMemo } from "react";

import {
  useCanUseTelegramSites,
  useCanUseTwitter,
  useCanUseTwitterSites,
} from "@/channels/access";

/**
 * Capabilities d'organisation — V1 STUB.
 *
 * Pas de table de capabilities par org : toute org « possède » les briques de
 * base, et les canaux à accès accordé (Twitter, Twitter — Sites, Telegram —
 * Sites) s'ajoutent selon `useCanUse*` (propriétaire OU accès accordé, table
 * `appAccess`). Elles sont lues par `isPanelVisible` (`shell/panelVisibility.ts`)
 * pour le rail du Bureau (`shell/ecransDuBureau.ts`) et le picker
 * (`shell/PanelPicker.tsx`). COUTURE : une vraie source remplace SEULEMENT le
 * corps de ce hook.
 */
export const BASE_CAPABILITIES = [
  "telegram",
  "creators",
  "vault",
  "conversations",
  "orders",
  "analytics",
] as const;

export function useCapabilities(): {
  capabilities: Set<string> | null;
  isLoading: boolean;
} {
  const canUseTwitter = useCanUseTwitter();
  const canUseTwitterSites = useCanUseTwitterSites();
  const canUseTelegramSites = useCanUseTelegramSites();
  return useMemo(() => {
    const set = new Set<string>(BASE_CAPABILITIES);
    // Twitter : propriétaire OU accès accordé (cf table `appAccess`).
    if (canUseTwitter) set.add("twitter");
    // Twitter — Sites : capability À PART, accordée séparément. C'est elle
    // qui fait que les deux canaux s'allument indépendamment dans le rail du
    // Bureau et le picker.
    if (canUseTwitterSites) set.add("twitter-sites");
    // Telegram — Sites : capability À PART elle aussi. ⚠️ Noter l'asymétrie
    // avec `telegram` tout court, qui est dans BASE_CAPABILITIES : le canal
    // des créatrices est ouvert à toute org, celui des sites s'accorde.
    if (canUseTelegramSites) set.add("telegram-sites");
    return { capabilities: set, isLoading: false };
  }, [canUseTwitter, canUseTwitterSites, canUseTelegramSites]);
}

/** True si `set` contient toutes les capabilities requises (vide/undefined = OK). */
export function hasAllCapabilities(
  set: Set<string> | null,
  required: readonly string[] | undefined,
): boolean {
  if (!required || required.length === 0) return true;
  if (!set) return false;
  return required.every((c) => set.has(c));
}
